import {
  AIStarsIcon,
  AdminCustomSelect,
  AdminInput,
  Button,
  Div,
  ErrorToast,
  FileDropzone,
  ModalPortal,
  PropsModal,
  SimpleBrandWithImageEntity,
  getMenu,
  reportError,
} from '@vgrubs/components';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import { MenuV2Response } from 'components/src/types/menu.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  ImportBrandPicker,
  ImportLocationPicker,
} from './ImportFromStorePicker';
import { PromptTextarea } from './PromptTextarea';

export enum ImportMethod {
  existingStore = 'existingStore',
  menuLink = 'menuLink',
  ai = 'ai',
}

const importMethodOptions = [
  { name: 'Import by Menu Link', value: ImportMethod.menuLink },
  { name: 'Import from Existing Store', value: ImportMethod.existingStore },
  { name: 'Generate with AI', value: ImportMethod.ai },
];

const defaultImportMethod = importMethodOptions[0].value;

export const ImportMenuModal = ({
  importMenuModal,
  closeModal,
  setProviderToImport,
  setUrlToScrape,
  setMenuToImport,
  setLoading,
  startAiImport,
}: PropsModal & {
  importMenuModal: boolean;
  setProviderToImport: Dispatch<SetStateAction<string | null>>;
  setUrlToScrape: Dispatch<SetStateAction<string | null>>;
  setMenuToImport: (
    menu: MenuV2Response,
    source: { locationId: number; brandId: number | null }
  ) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  startAiImport: (file: File, subPrompt: string) => void;
}) => {
  const [importMethod, setImportMethod] =
    useState<ImportMethod>(defaultImportMethod);
  const [menuLink, setMenuLink] = useState('');
  const [selectedLocation, setSelectedLocation] =
    useState<SimpleLocationEntity | null>(null);
  const [selectedBrand, setSelectedBrand] =
    useState<SimpleBrandWithImageEntity | null>(null);
  const [aiFile, setAiFile] = useState<File | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');

  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  useEffect(() => {
    setImportMethod(defaultImportMethod);
    setMenuLink('');
    setSelectedLocation(null);
    setSelectedBrand(null);
    setAiFile(null);
    setAiPrompt('');
  }, [importMenuModal]);

  useEffect(() => {
    setSelectedBrand(null);
  }, [selectedLocation]);

  async function handleSubmitMenuLink() {
    if (!menuLink) return ErrorToast('Please Enter Menu Link.');
    closeModal?.();
    setProviderToImport('Menu Link');
    setUrlToScrape(menuLink);
  }

  async function handleSubmitExistingStore() {
    if (!selectedLocation) return ErrorToast('Please Select Location.');

    const isSameStore =
      selectedLocation.id === Number(locationId) &&
      (selectedBrand?.id || 0) === Number(brandId);

    if (isSameStore) {
      return ErrorToast(
        "You can't import a store's menu into the same store. Please select a different location or brand to import from."
      );
    }

    try {
      setLoading(true);
      const { data } = await getMenu(
        selectedLocation.id,
        selectedBrand?.id ?? 0
      );
      setMenuToImport(data.data, {
        locationId: selectedLocation.id,
        brandId: selectedBrand?.id ?? null,
      });
      closeModal?.();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmitAi() {
    if (!aiFile) return ErrorToast('Please upload a document.');
    closeModal?.();
    startAiImport(aiFile, aiPrompt);
  }

  const handleSubmit = () => {
    if (importMethod === ImportMethod.menuLink) return handleSubmitMenuLink();
    if (importMethod === ImportMethod.ai) return handleSubmitAi();
    return handleSubmitExistingStore();
  };

  // Icons are attached here (not at module level) so the AIStarsIcon JSX is
  // created during render, not at import time.
  const importMethodOptionsWithIcons = importMethodOptions.map((option) =>
    option.value === ImportMethod.ai
      ? { ...option, icon: <AIStarsIcon /> }
      : option
  );

  return (
    <ModalPortal
      header="Import Menu"
      isOpen={importMenuModal}
      closeModal={closeModal}
      logo={false}
      overflow="visible"
    >
      <AdminCustomSelect<ImportMethod>
        value={importMethod}
        onChange={(value) => {
          if (value && !Array.isArray(value)) setImportMethod(value);
        }}
        options={importMethodOptionsWithIcons}
        placeholder="Select Import Method"
        $shadowDesign={false}
      />

      {importMethod === ImportMethod.menuLink && (
        <Div $mt="16">
          <AdminInput
            value={menuLink}
            onChange={({ target }) => setMenuLink(target.value)}
            placeholder="Enter the menu link"
            $textCenter
          />
        </Div>
      )}

      {importMethod === ImportMethod.existingStore && (
        <>
          <Div $mt="16">
            <ImportLocationPicker
              selectedLocation={selectedLocation}
              onSelect={setSelectedLocation}
            />
          </Div>
          <Div $mt="16">
            <ImportBrandPicker
              brands={selectedLocation?.brands || []}
              selectedBrand={selectedBrand}
              onSelect={setSelectedBrand}
              disabled={!selectedLocation}
              clearable
            />
          </Div>
        </>
      )}

      {importMethod === ImportMethod.ai && (
        <>
          <Div $mt="16">
            <FileDropzone file={aiFile} onFileChange={setAiFile} />
          </Div>
          <Div $mt="16">
            <PromptTextarea
              label="Prompt (optional)"
              placeholder="e.g. translate descriptions to Spanish, raise all prices 10%"
              value={aiPrompt}
              onChange={({ target }) => setAiPrompt(target.value)}
              $textareaHeight="100"
              expandTitle="Edit prompt"
            />
          </Div>
        </>
      )}

      <Button
        $selected
        $blueGradient={importMethod === ImportMethod.ai}
        $w="100%"
        $mt="32"
        onClick={handleSubmit}
      >
        {importMethod === ImportMethod.ai ? (
          <Div $flex $center $alignCenter $gap="8px">
            <AIStarsIcon />
            Generate Menu with AI
          </Div>
        ) : (
          'Import Menu'
        )}
      </Button>
    </ModalPortal>
  );
};
